import React, { useEffect, useState, useRef, useCallback } from 'react';
import MuiTable from 'components/molecules/MuiTable';
import { MUIDataTableColumnDef, MUIDataTableOptions, MUIDataTableState } from 'mui-datatables';
import textLabels from 'utils/MUIDataTableTextLabels';
import { useForm } from 'react-hook-form';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import useProductsIM from 'hooks/useProductsIM';
import useMovementsIM from 'hooks/useMovementsIM';
import { Button, TextField, CircularProgress, IconButton, Grid } from '@material-ui/core';
import useCustomSnackbar from 'hooks/useCustomSnackbar';
import { AJUSTE } from 'constants/constBd';
import mapVariationToProduct from 'utils/mapVariationToProduct';
import { Pagination } from 'interfaces/pagination.interface';
import { IProductList, IProducts } from 'interfaces/imProducts.interface';
import { FormEntry, IDocument, IFormAdjustment, IProductsAdjustment } from 'interfaces/imInventoryMovements.interface';

export default function IMAdjustmentList() {
  const productsSelectedUser: IProducts[] = [];

  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({ limit: 10, page: 1 });
  const [searchText, setSearchText] = useState('');
  const [productList, setProductsList] = useState<IProducts[]>([]);
  const [productsSelected, setProductsSelected] = useState<IProducts[]>([]);
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const { getProducts } = useProductsIM();
  const { handleSubmit } = useForm<FormEntry>();
  const { showNotistack } = useCustomSnackbar();
  const { createMovement } = useMovementsIM();

  const getProductBySearch = useCallback(() => {
    setIsLoading(true);
    getProducts(pagination.page, pagination.limit, searchText)
      .then((result: IProductList) => {
        setProductsList(mapVariationToProduct(result.products));
        setTotalProducts(result.total);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [pagination, isEmpty]);

  const validateFieldTable = () => {
    const validateFields = productsSelected.some((product) => {
      if (product.stock) {
        return true;
      } else {
        return false;
      }
    });

    return validateFields;
  };

  const deleteProductSelected = (index: number) => {
    const productNewArray: IProducts[] = [...productsSelected];
    productNewArray.splice(index, 1);
    setProductsSelected(productNewArray);
  };

  useEffect(() => {
    getProductBySearch();
  }, [getProductBySearch]);

  useEffect(() => {
    if (inputRef && inputRef.current) {
      inputRef?.current.focus();
    }
  }, [isLoading]);

  useEffect(() => {
    showNotistack({ message: 'Si la cantidad es vacía equivale a 0' });
  }, []);
  const options: MUIDataTableOptions = {
    print: false,
    filter: false,
    serverSide: true,
    count: totalProducts,
    viewColumns: false,
    download: false,
    rowHover: true,
    searchOpen: true,
    rowsPerPageOptions: [10, 20, 50, 100],
    textLabels,
    selectableRows: 'multiple',
    onRowSelectionChange: (currentRowsSelected: any[], allRowsSelected: any[]) => {
      allRowsSelected.map((item: any) => {
        const productSelectedChange = productList[item.index];
        productsSelectedUser.push(productSelectedChange);
      });
      const concatArray = Array.from(new Set(productsSelected.concat(productsSelectedUser)));
      setProductsSelected(concatArray);
    },
    onTableChange: (action: string, tableState: MUIDataTableState) => {
      if (action === 'changePage' || action === 'changeRowsPerPage') {
        const newPagination: Pagination = {
          ...pagination,
          limit: tableState.rowsPerPage,
          page: tableState.page + 1
        };
        setPagination(newPagination);
      }
    },
    customSearchRender: (searchTextMui: string, handleSearch, hideSearch) => {
      return (
        <Grid container justify="flex-start" direction="row">
          <TextField
            autoFocus
            variant="outlined"
            margin="normal"
            inputRef={inputRef}
            name="Search"
            label="Buscar"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                setIsEmpty(!isEmpty);
              }
            }}
            onChange={(e) => setSearchText(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {isLoading && (
            <CircularProgress
              size={24}
              style={{ marginLeft: 15, position: 'relative', marginTop: 'auto', marginBottom: 'auto' }}
            />
          )}
          <IconButton
            onClick={() => {
              setSearchText('');
              hideSearch();
              setIsEmpty(!isEmpty);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Grid>
      );
    },
    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => {
      return (
        <Grid container justify="flex-start" direction="row">
          <TextField
            autoFocus
            variant="outlined"
            margin="normal"
            name="Search"
            label="Buscar"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                setIsEmpty(!isEmpty);
                setSelectedRows([]);
              }
            }}
            onChange={(e) => setSearchText(e.target.value)}
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
          />
          {isLoading && (
            <CircularProgress
              size={24}
              style={{ marginLeft: 15, position: 'relative', marginTop: 'auto', marginBottom: 'auto' }}
            />
          )}
          <IconButton
            onClick={() => {
              setSearchText('');
              setSelectedRows([]);
              setIsEmpty(!isEmpty);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Grid>
      );
    },
    isRowSelectable: () => {
      if (!isLoading && productList.length > 0) {
        return true;
      } else {
        return false;
      }
    }
  };

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'productId',
      options: {
        display: false,
        download: false,
        print: false,
        filter: false,
        sort: false
      }
    },
    {
      name: 'name',
      label: 'Producto',
      options: {
        customBodyRender: (value) => <div style={{ fontSize: 12 }}>{value}</div>
      }
    },

    {
      name: 'sku',
      label: 'SKU',
      options: {
        customBodyRender: (value) => <div style={{ fontSize: 12 }}>{value}</div>
      }
    },
    {
      name: 'type',
      label: 'Tipo',
      options: {
        customBodyRender: (value) => <div style={{ fontSize: 12 }}>{value}</div>
      }
    },
    {
      name: 'stock',
      label: 'Cantidad',
      options: {
        customBodyRender: (value) => <div style={{ fontSize: 12 }}>{value}</div>
      }
    }
  ];

  const optionsNew: MUIDataTableOptions = {
    print: false,
    filter: false,
    viewColumns: false,
    download: false,
    rowHover: true,
    rowsPerPage: 3,
    rowsPerPageOptions: [],
    textLabels,
    selectableRows: 'none'
  };

  const columnsNew: MUIDataTableColumnDef[] = [
    {
      name: 'productId',
      options: {
        display: false,
        download: false,
        print: false,
        filter: false,
        sort: false
      }
    },
    {
      name: 'name',
      label: 'Producto'
    },

    {
      name: 'sku',
      label: 'SKU'
    },
    {
      name: 'type',
      label: 'Tipo'
    },
    {
      name: 'stock',
      label: 'Cantidad',
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          const newArrProducts = [...productsSelected];

          const productIndex = newArrProducts.findIndex((item) => item.productId === tableMeta.rowData[0]);

          return (
            <TextField
              required
              variant="outlined"
              margin="normal"
              fullWidth
              name="stock"
              label="Cantidad"
              disabled={isLoading}
              InputLabelProps={{ shrink: true }}
              value={newArrProducts[productIndex]?.stock || ''}
              onChange={(e) => {
                const validateQuantity = /^[0-9_]*$/;
                if (e.target.value.match(validateQuantity)) {
                  updateValue(e.target.value);
                  productsSelected[productIndex].stock = e.target.value;
                }
              }}
            />
          );
        }
      }
    },
    {
      name: '',
      label: 'Eliminar',
      options: {
        filter: false,
        sort: false,
        download: false,
        print: false,
        empty: true,
        customBodyRender: (value, tableMeta) => {
          return (
            <Grid container justify="flex-start">
              <IconButton
                style={{ color: 'red' }}
                onClick={() => {
                  deleteProductSelected(tableMeta.rowIndex);
                  setIsEmpty(!isEmpty); // Verificar que tan optimo es.
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Grid>
          );
        }
      }
    }
  ];

  const onSubmit = (data: FormEntry, e: any) => {
    const validate = validateFieldTable();

    if (!validate) {
      showNotistack({ message: 'La cantidad es obligatoria' });
      return;
    } else {
      const orderDataProduct = productsSelected.map((product) => {
        const productFinal: IProductsAdjustment = {
          parentId: product.parentId,
          productId: product.productId,
          unitCost: 0,
          quantity: product.stock ? product.stock : '0'
        };

        return productFinal;
      });

      const document: IDocument = {
        remission: '',
        invoice: '',
        purchaseOrder: '',
        order: '',
        guide: '',
        observations: '',
        documentTypeId: AJUSTE
      };

      const dataToSend: IFormAdjustment[] = [{ document: document, products: orderDataProduct }];

      setIsLoading(true);
      createMovement(dataToSend)
        .then((response: boolean) => {
          if (response === true) {
            showNotistack({ message: 'El ajuste se guardó correctamente' });
            setProductsSelected([]);
            getProductBySearch();
            e.target.reset();
          } else {
            showNotistack({ message: 'Ocurrió un error' });
          }
        })
        .catch((err: any) => {
          console.log(err.reponse?.data);
          showNotistack({ message: 'Ocurrió un error' });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <Grid container justify="space-around" direction="row">
      <Grid item xs={12} sm={12} md={12} lg={5}>
        <Grid container justify="center">
          <MuiTable
            title="Lista de productos"
            isLoading={isLoading}
            columns={columns}
            data={productList}
            options={options}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={6}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <MuiTable
            title="Lista de productos seleccionados"
            isLoading={isLoading}
            columns={columnsNew}
            data={productsSelected}
            options={optionsNew}
          />
          <Grid container justify="center">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading || productsSelected.length === 0}
              style={{ marginTop: 10 }}
            >
              {isLoading ? <CircularProgress size={24} /> : ''} Guardar
            </Button>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
}
